import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp }  from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import UpgradeBanner from '../components/UpgradeBanner';

function isPlanLimit(msg: string) { return msg.startsWith('Free plan'); }

const API_URL = import.meta.env.VITE_API_URL as string;

export default function LeagueSettings() {
  const { league, service } = useApp();
  const { getToken }        = useAuth();
  const { leagueId }        = useParams<{ leagueId: string }>();
  const navigate            = useNavigate();

  // ── League settings ──────────────────────────────────────────────────────
  const [name,        setName]        = useState(league?.name ?? '');
  const [maxPlayers,  setMaxPlayers]  = useState<number | ''>(league?.maxPlayers ?? 32);
  const [courts,      setCourts]      = useState<number | ''>(league?.courts ?? 4);
  const [leagueSaved, setLeagueSaved] = useState(false);
  const [leagueError, setLeagueError] = useState('');

  // ── Delegation token ─────────────────────────────────────────────────────
  const [delegateHours,  setDelegateHours]  = useState(168);  // 7 days
  const [generatedToken, setGeneratedToken] = useState('');
  const [tokenExpiry,    setTokenExpiry]    = useState(0);    // Unix epoch secs
  const [tokenLoading,   setTokenLoading]   = useState(false);
  const [tokenError,     setTokenError]     = useState('');
  const [revoking,       setRevoking]       = useState(false);
  const [revokeSuccess,  setRevokeSuccess]  = useState(false);

  // ── Reset request ─────────────────────────────────────────────────────────
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetReason,    setResetReason]    = useState('');
  const [resetSent,      setResetSent]      = useState(false);

  // ── Archive ───────────────────────────────────────────────────────────────
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveConfirm,   setArchiveConfirm]   = useState('');
  const [archiving,        setArchiving]        = useState(false);
  const [archiveError,     setArchiveError]     = useState('');

  // ── Load existing token on mount ──────────────────────────────────────────

  useEffect(() => {
    if (!leagueId) return;
    (async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}/leagues/${leagueId}/delegate`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as { token: string; expiresAt: number };
          setGeneratedToken(data.token);
          setTokenExpiry(data.expiresAt);
        }
      } catch { /* no active token — leave form empty */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function saveLeague() {
    if (!league || !name.trim()) return;
    setLeagueError('');
    try {
      await service.setLeague({ ...league, name: name.trim(), maxPlayers: Number(maxPlayers) || 32, courts: Number(courts) || 4 });
      setLeagueSaved(true);
      setTimeout(() => setLeagueSaved(false), 2000);
    } catch (e) {
      setLeagueError(e instanceof Error ? e.message : 'Failed to save settings.');
    }
  }

  async function generateToken() {
    setTokenError('');
    setTokenLoading(true);
    setRevokeSuccess(false);
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/leagues/${leagueId}/delegate`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ expiresInHours: delegateHours }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? 'Failed to generate token');
      }
      const data = await res.json() as { token: string; expiresAt: number };
      setGeneratedToken(data.token);
      setTokenExpiry(data.expiresAt);
    } catch (err) {
      setTokenError(err instanceof Error ? err.message : 'Failed to generate token');
    } finally {
      setTokenLoading(false);
    }
  }

  async function revokeToken() {
    setTokenError('');
    setRevoking(true);
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/leagues/${leagueId}/delegate`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to revoke token');
      setGeneratedToken('');
      setTokenExpiry(0);
      setRevokeSuccess(true);
      setTimeout(() => setRevokeSuccess(false), 3000);
    } catch (err) {
      setTokenError(err instanceof Error ? err.message : 'Failed to revoke token');
    } finally {
      setRevoking(false);
    }
  }

  async function archiveLeague() {
    if (archiveConfirm !== league?.name) return;
    setArchiveError('');
    setArchiving(true);
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/leagues/${leagueId}/archive`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to archive league');
      navigate('/leagues');
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to archive league');
      setArchiving(false);
    }
  }

  function submitReset() {
    setResetSent(true);
    setTimeout(() => { setShowResetModal(false); setResetSent(false); setResetReason(''); }, 3000);
  }

  async function copyToClipboard(text: string) {
    try { await navigator.clipboard.writeText(text); } catch { /* silent */ }
  }

  const kioskBaseUrl = `${window.location.origin}/kiosk/${leagueId}`;
  const expiryDate   = tokenExpiry ? new Date(tokenExpiry * 1000).toLocaleDateString() : '';

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>

      {/* League settings */}
      <div className="card">
        <div className="card-title">⚙ League Settings</div>
        {leagueSaved && <div className="alert alert-success">✅ Settings saved.</div>}
        {leagueError && isPlanLimit(leagueError)
          ? <UpgradeBanner message={leagueError} />
          : leagueError && <div className="alert alert-red">{leagueError}</div>
        }
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label>League Name</label>
            <input value={name} onChange={e => setName(e.target.value)} style={{ marginTop: 4 }} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label>Max Players</label>
              <input type="number" min={4} max={500} value={maxPlayers} onChange={e => setMaxPlayers(e.target.value === '' ? '' : parseInt(e.target.value))} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label>Default Courts</label>
              <input type="number" min={1} max={20} value={courts} onChange={e => setCourts(e.target.value === '' ? '' : parseInt(e.target.value))} />
              <span className="hint">Sessions can override this per-session.</span>
            </div>
          </div>
        </div>
        <button
          className="btn btn-primary"
          style={{ marginTop: 14 }}
          onClick={saveLeague}
          disabled={league?.status === 'archived'}
        >
          Save Settings
        </button>
        {league?.status === 'archived' && (
          <p style={{ marginTop: 8, fontSize: '.82rem', color: 'var(--gray-500)' }}>
            This league is archived — settings are read-only.
          </p>
        )}
      </div>

      {/* Kiosk access token */}
      <div className="card">
        <div className="card-title">📱 Kiosk Links</div>
        <p style={{ fontSize: '.875rem', color: 'var(--gray-700)', marginBottom: 14 }}>
          Generate a time-limited token to share with players and helpers. Only one token is active at a time —
          generating a new one immediately invalidates the previous.
        </p>

        {revokeSuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>Token revoked. Kiosk links are now inactive.</div>}
        {tokenError    && <div className="alert alert-red"    style={{ marginBottom: 12 }}>{tokenError}</div>}

        {generatedToken ? (
          <>
            <div className="alert alert-success" style={{ marginBottom: 14 }}>
              Token valid until <strong>{expiryDate}</strong>. Copy the links below — the token won't be shown again after you leave this page.
            </div>

            {(['register', 'scores', 'session'] as const).map(path => {
              const label = path === 'register' ? '🚪 Registration Kiosk'
                          : path === 'scores'   ? '🏅 Score Entry Kiosk'
                          :                       '🎮 Delegate Session';
              const url   = `${kioskBaseUrl}/${path}?token=${generatedToken}`;
              return (
                <div key={path} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '.82rem', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <code style={{
                      background: 'var(--gray-100)', padding: '6px 8px', borderRadius: 4,
                      fontSize: '.72rem', flex: 1, wordBreak: 'break-all', lineHeight: 1.5,
                    }}>
                      {url}
                    </code>
                    <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }} onClick={() => copyToClipboard(url)}>
                      Copy
                    </button>
                    <a href={url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}>
                      Open ↗
                    </a>
                  </div>
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setGeneratedToken(''); setTokenExpiry(0); }}>
                Done
              </button>
              <button className="btn btn-danger btn-sm" onClick={revokeToken} disabled={revoking}>
                {revoking ? 'Revoking…' : 'Revoke Token'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label>Duration</label>
              <select value={delegateHours} onChange={e => setDelegateHours(parseInt(e.target.value))}>
                <option value={24}>1 day</option>
                <option value={72}>3 days</option>
                <option value={168}>7 days</option>
                <option value={336}>14 days</option>
                <option value={720}>30 days</option>
              </select>
            </div>
            <button
              className="btn btn-primary"
              onClick={generateToken}
              disabled={tokenLoading || league?.status === 'archived'}
            >
              {tokenLoading ? 'Generating…' : 'Generate Token'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={revokeToken} disabled={revoking}>
              {revoking ? 'Revoking…' : 'Revoke Existing'}
            </button>
          </div>
        )}
      </div>

      {/* Danger zone */}
      <details style={{ border: '1.5px solid var(--red-l)', borderRadius: 8, overflow: 'hidden' }}>
        <summary style={{
          padding: '14px 16px',
          cursor: 'pointer',
          fontWeight: 700,
          color: 'var(--red)',
          background: '#fff',
          listStyle: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          userSelect: 'none',
        }}>
          ⚠️ Danger Zone
          <span style={{ marginLeft: 'auto', fontSize: '.78rem', fontWeight: 400, color: 'var(--gray-400)' }}>
            click to expand
          </span>
        </summary>

        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--red-l)' }}>
          {/* Archive */}
          <div style={{ marginTop: 16, marginBottom: 20 }}>
            <p style={{ fontSize: '.875rem', fontWeight: 600, marginBottom: 4 }}>Archive League</p>
            <p style={{ fontSize: '.875rem', color: 'var(--gray-700)', marginBottom: 10 }}>
              Permanently locks the league — all data is preserved read-only but no new sessions,
              players, or games can be added. This cannot be undone.
            </p>
            {league?.status === 'archived' ? (
              <div className="alert alert-warn">This league is archived.</div>
            ) : (
              <button className="btn btn-danger btn-sm" onClick={() => setShowArchiveModal(true)}>
                Archive League
              </button>
            )}
          </div>

          {/* Reset request */}
          <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
            <p style={{ fontSize: '.875rem', fontWeight: 600, marginBottom: 4 }}>Request Data Reset</p>
            <p style={{ fontSize: '.875rem', color: 'var(--gray-700)', marginBottom: 10 }}>
              Clears all players, games, and session history. Requires admin approval.
            </p>
            <button className="btn btn-danger btn-sm" onClick={() => setShowResetModal(true)}>
              Request League Reset
            </button>
          </div>
        </div>
      </details>

      {/* Archive confirmation modal */}
      {showArchiveModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 460, width: '100%' }}>
            <h3 style={{ fontWeight: 800, color: 'var(--red)', marginBottom: 8 }}>Archive League?</h3>
            <p style={{ fontSize: '.875rem', color: 'var(--gray-700)', marginBottom: 14 }}>
              This is permanent and irreversible. Type <strong>{league?.name}</strong> to confirm.
            </p>
            <input
              value={archiveConfirm}
              onChange={e => setArchiveConfirm(e.target.value)}
              placeholder={league?.name}
              style={{ marginBottom: 12 }}
            />
            {archiveError && <div className="alert alert-red" style={{ marginBottom: 10 }}>{archiveError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowArchiveModal(false); setArchiveConfirm(''); setArchiveError(''); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                disabled={archiveConfirm !== league?.name || archiving}
                onClick={archiveLeague}
              >
                {archiving ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset modal */}
      {showResetModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 460, width: '100%' }}>
            {resetSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>📨</div>
                <h3 style={{ fontWeight: 800, marginBottom: 6 }}>Request Submitted</h3>
                <p style={{ color: 'var(--gray-500)', fontSize: '.875rem' }}>
                  An admin will review your request and reach out before resetting.
                </p>
              </div>
            ) : (
              <>
                <h3 style={{ fontWeight: 800, color: 'var(--red)', marginBottom: 8 }}>Request League Reset</h3>
                <p style={{ fontSize: '.875rem', color: 'var(--gray-700)', marginBottom: 14 }}>
                  Tell us why you need a full reset. An admin will review and confirm before any data is deleted.
                </p>
                <textarea
                  rows={3}
                  value={resetReason}
                  onChange={e => setResetReason(e.target.value)}
                  placeholder="Reason for reset (e.g. starting a new season)"
                  style={{ marginBottom: 14 }}
                />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setShowResetModal(false)}>Cancel</button>
                  <button className="btn btn-danger" onClick={submitReset} disabled={!resetReason.trim()}>
                    Submit Request
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
